/**
 * Sales Data tab of the Admin User Editor (AR-327) — entirely read-only.
 *
 * Commission and production figures come from the statements/TRAMS reporting
 * feed, which is not wired to this page yet, so the tiles render their labels
 * with an empty value rather than a plausible-looking number.
 *
 * AR-382: bound to the migrated model, the four figures are computed from the
 * weekly statements (finance) — personal for the member, and across every
 * member of their team for the agency. GEM Level by Year has no history
 * column and stays empty.
 */
import React from 'react';
import { EM_DASH } from '@/lib/adminUserRecordApi';
import type { AdminRecordMigrated, AdminRecordSales } from '@/lib/adminUserRecordApi';
import { PendingIntegration, RecordCard } from './recordPrimitives';

const PERFORMANCE_METRICS: { label: string; key: keyof AdminRecordSales }[] = [
  { label: 'Last-Year Total Commission', key: 'lastYearCommission' },
  { label: 'YTD Commission', key: 'ytdCommission' },
  { label: 'Last-Year Total Sales', key: 'lastYearSales' },
  { label: 'YTD Sales', key: 'ytdSales' },
];

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function PerformanceGrid({ figures }: { figures: AdminRecordSales | null | undefined }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {PERFORMANCE_METRICS.map(({ label, key }) => (
        <StatTile key={label} label={label} value={figures ? usd.format(figures[key]) : EM_DASH} />
      ))}
    </div>
  );
}

const GEM_YEARS = [4, 3, 2, 1, 0].map((offset) => new Date().getFullYear() - offset);

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3 dark:bg-gray-900">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  );
}

export function SalesDataTab({
  gemStatus,
  sales,
}: {
  gemStatus: string | null;
  sales?: AdminRecordMigrated['sales'];
}) {
  const asOf = sales ? `Read-only · from weekly statements, as of ${sales.asOf}.` : 'Read-only.';
  return (
    <div className="flex flex-col gap-5">
      {!sales && <PendingIntegration source="the commission + production reporting feed" />}

      <RecordCard title="Individual Performance" description={asOf}>
        <PerformanceGrid figures={sales?.individual} />
      </RecordCard>

      <RecordCard
        title="Agency Performance"
        description={sales && !sales.agency ? 'Read-only · the member is on no team.' : asOf}
      >
        <PerformanceGrid figures={sales?.agency} />
      </RecordCard>

      <RecordCard
        title="GEM Level by Year"
        description={gemStatus ? `Current GEM status: ${gemStatus}.` : 'Read-only.'}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {GEM_YEARS.map((year) => (
            <div
              key={year}
              className="rounded-lg border border-slate-200 px-3 py-3 text-center dark:border-gray-700"
            >
              <p className="text-xs text-slate-500 dark:text-slate-400">{year}</p>
              <p className="mt-1 text-xl font-semibold text-orange-600 dark:text-orange-400">
                {EM_DASH}
              </p>
            </div>
          ))}
        </div>
      </RecordCard>
    </div>
  );
}
